import { useOrderStats } from '../hooks/useWpApi';
import { SilviaLoader } from '../components/SilviaLoader';
import { RefreshCw, ShoppingCart, AlertTriangle, BarChart3, TrendingUp, TrendingDown, Package, Clock } from 'lucide-react';
import '../styles/OrderAnalytics.css';

export function OrderAnalytics() {
  const { data, loading, error, refresh, lastRefresh } = useOrderStats(14, 120_000);

  if (loading && !data) {
    return <SilviaLoader text="Ładowanie zamówień..." />;
  }

  if (error && !data) {
    return (
      <div className="stats-error">
        <AlertTriangle size={32} />
        <p>{error}</p>
        <button className="btn-refresh" onClick={refresh}>Spróbuj ponownie</button>
      </div>
    );
  }

  if (!data) return null;

  const { today, pending_orders, daily_chart, delayed_packages, sales_comparison } = data;
  const allOrdered = pending_orders.total === 0;

  // Chart calculations
  const maxOrders = Math.max(...daily_chart.map(d => d.total), 1);
  const maxRevenue = Math.max(...daily_chart.map(d => d.revenue), 1);

  return (
    <div className="stats-page">
      <div className="stats-header">
        <div className="stats-header-top">
          <h1>📦 Zamówienia</h1>
          <button className="btn-icon-refresh" onClick={refresh} title="Odśwież">
            <RefreshCw size={18} className={loading ? 'spinning' : ''} />
          </button>
        </div>
        {lastRefresh && (
          <span className="stats-updated">Aktualizacja: {lastRefresh.toLocaleTimeString('pl-PL')}</span>
        )}
      </div>

      {/* ── Today Hero ── */}
      <section className="stats-section">
        <div className="orders-hero">
          <div className="orders-hero-stat">
            <span className="orders-hero-icon">🛍️</span>
            <span className="orders-hero-number">{today.orders_count}</span>
            <span className="orders-hero-label">zamówień dziś</span>
          </div>
          <div className="orders-hero-divider" />
          <div className="orders-hero-stat">
            <span className="orders-hero-number">{today.orders_amount.toFixed(0)} zł</span>
            <span className="orders-hero-label">przychód</span>
          </div>
        </div>
        {sales_comparison.change_pct !== 0 && (
          <div className={`comparison-badge ${sales_comparison.change_pct > 0 ? 'positive' : 'negative'}`}>
            {sales_comparison.change_pct > 0 ? <TrendingUp size={14} /> : <TrendingDown size={14} />}
            <span>{sales_comparison.change_pct > 0 ? '+' : ''}{sales_comparison.change_pct}% vs rok temu</span>
          </div>
        )}
      </section>

      {/* ── Remaining Orders ── */}
      <section className="stats-section">
        <h2 className="section-title"><ShoppingCart size={18} />Do zamówienia</h2>
        {allOrdered ? (
          <div className="celebration-card">
            <span className="celebration-emoji">🏝️</span>
            <span>Wszystko zamówione!</span>
          </div>
        ) : (
          <div className="remaining-grid">
            <div className="remaining-total">
              <span className="remaining-total-num">{pending_orders.total}</span>
              <span className="remaining-total-label">do zamówienia</span>
              <span className="remaining-total-cost">{pending_orders.total_cost.toFixed(0)} zł</span>
            </div>
            <div className="remaining-breakdown">
              <RemItem icon="💳" label="Opłacone" count={pending_orders.prepaid} />
              <RemItem icon="🧊" label="Pobranie" count={pending_orders.cod} />
              <RemItem icon="🚚" label="DHL" count={pending_orders.dhl} />
              {pending_orders.low_stock > 0 && <RemItem icon="🚨" label="Low stock" count={pending_orders.low_stock} alert />}
              {pending_orders.complaints > 0 && <RemItem icon="⚠️" label="Reklamacje" count={pending_orders.complaints} alert />}
              {pending_orders.return_stock > 0 && <RemItem icon="🏪" label="Ze zwrotów" count={pending_orders.return_stock} />}
            </div>
          </div>
        )}

        {delayed_packages > 0 && (
          <div className="alert-card warning">
            <Clock size={16} />
            <span>{delayed_packages} paczek czeka &gt;24h na wysyłkę</span>
          </div>
        )}
      </section>

      {/* ── Daily Order Chart ── */}
      <section className="stats-section">
        <h2 className="section-title"><BarChart3 size={18} />Ostatnie 14 dni</h2>
        
        {/* Orders chart */}
        <div className="chart-container">
          <h3 className="chart-subtitle">Zamówienia</h3>
          <div className="bar-chart">
            {daily_chart.map((day, i) => {
              const isToday = i === daily_chart.length - 1;
              return (
                <div key={i} className={`bar-col ${isToday ? 'today' : ''}`}>
                  <div className="bar-value">{day.total}</div>
                  <div className="bar-wrapper">
                    <div className="bar-segment completed" style={{ height: `${(day.completed / maxOrders) * 100}%` }} />
                    <div className="bar-segment processing" style={{ height: `${(day.processing / maxOrders) * 100}%` }} />
                    <div className="bar-segment onhold" style={{ height: `${(day.onhold / maxOrders) * 100}%` }} />
                    {day.failed > 0 && (
                      <div className="bar-segment failed" style={{ height: `${Math.max((day.failed / maxOrders) * 100, 3)}%` }} />
                    )}
                  </div>
                  <span className="bar-label">{day.label.split(' ')[0]}</span>
                  <span className="bar-date">{day.label.split(' ')[1]}</span>
                </div>
              );
            })}
          </div>
          <div className="chart-legend">
            <span className="legend-item"><span className="legend-dot completed" />Zakończone</span>
            <span className="legend-item"><span className="legend-dot processing" />Przetwarzanie</span>
            <span className="legend-item"><span className="legend-dot onhold" />On-hold</span>
            <span className="legend-item"><span className="legend-dot failed" />Błędy</span>
          </div>
        </div>

        {/* Revenue chart */}
        <div className="chart-container">
          <h3 className="chart-subtitle">Przychód (zł)</h3>
          <div className="bar-chart">
            {daily_chart.map((day, i) => {
              const barH = Math.max((day.revenue / maxRevenue) * 100, 5);
              const isToday = i === daily_chart.length - 1;
              return (
                <div key={i} className={`bar-col ${isToday ? 'today' : ''}`}>
                  <div className="bar-value">{(day.revenue / 1000).toFixed(1)}k</div>
                  <div className="bar-wrapper">
                    <div className="bar-segment revenue" style={{ height: `${barH}%` }} />
                  </div>
                  <span className="bar-label">{day.label.split(' ')[0]}</span>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── Costs today ── */}
      <section className="stats-section">
        <h2 className="section-title"><Package size={18} />Koszty i marża (dziś)</h2>
        <div className="costs-summary">
          <div className="cost-row">
            <span className="cost-label">Meta Ads</span>
            <span className="cost-val">{today.meta_ads.toFixed(0)} zł</span>
          </div>
          <div className="cost-row">
            <span className="cost-label">Google Ads</span>
            <span className="cost-val">{today.google_ads.toFixed(0)} zł</span>
          </div>
          <div className="cost-row">
            <span className="cost-label">Dostawca</span>
            <span className="cost-val">{today.supplier_cost.toFixed(0)} zł</span>
          </div>
          <div className="cost-row total">
            <span className="cost-label">Razem koszty</span>
            <span className="cost-val">{today.total_costs.toFixed(0)} zł</span>
          </div>
          <div className={`cost-row margin ${today.margin >= 0 ? 'positive' : 'negative'}`}>
            <span className="cost-label">💰 Marża</span>
            <span className="cost-val">{today.margin.toFixed(0)} zł ({today.margin_pct}%)</span>
          </div>
        </div>
      </section>
    </div>
  );
}

function RemItem({ icon, label, count, alert }: { icon: string; label: string; count: number; alert?: boolean }) {
  return (
    <div className={`rem-item ${alert ? 'alert' : ''}`}>
      <span className="rem-icon">{icon}</span>
      <span className="rem-count">{count}</span>
      <span className="rem-label">{label}</span>
    </div>
  );
}
